import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Search, User, FolderKanban, StickyNote, ListChecks, Layers, ScrollText, History } from "lucide-react";

export default function GlobalSearch() {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const rootRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handler = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
        const term = q.trim();
        if (term.length < 2) { setData(null); return; }
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/search", { params: { q: term } });
                setData(data);
            } finally { setLoading(false); }
        }, 220);
        return () => clearTimeout(t);
    }, [q]);

    const go = (path) => { setOpen(false); setQ(""); navigate(path); };

    const groups = [
        { key: "clients", label: "Clienti", icon: User,
          render: (c) => ({ line1: `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—",
                            line2: c.company || c.email || "", to: `/clienti/${c.id}` }) },
        { key: "projects", label: "Progetti", icon: FolderKanban,
          render: (p) => ({ line1: p.title || "—",
                            line2: `${p.project_type === "continuous" ? "Continuativo" : "Spot"}${p.client_name ? " · " + p.client_name : ""}`,
                            to: `/progetti/${p.id}` }) },
        { key: "programs", label: "Programmi", icon: Layers,
          render: (p) => ({ line1: p.title || "—", line2: "Programma",
                            to: `/progetti/${p.project_id}` }) },
        { key: "tasks", label: "Task", icon: ListChecks,
          render: (t) => ({ line1: t.title || "—",
                            line2: `${t.level === "tertiary" ? "Task secondaria" : "Task"}${t.task_type ? " · " + t.task_type : ""}`,
                            to: `/progetti/${t.project_id}` }) },
        { key: "notes", label: "Note", icon: StickyNote,
          render: (n) => ({ line1: n.snippet || "—", line2: n.client_name || "",
                            to: `/clienti/${n.client_id}` }) },
        { key: "policies", label: "Policy", icon: ScrollText,
          render: (p) => {
              const rSuffix = p.revision > 0 ? ` R${p.revision}` : "";
              const pub = p.published_at ? String(p.published_at).slice(0,10).replaceAll("-","/") : "";
              return { line1: `${p.type}${pub ? " " + pub : ""} ${p.title || ""}${rSuffix}`,
                       line2: [p.series ? "Serie: " + p.series : null, p.status === "draft" ? "Bozza" : "Pubblicata"].filter(Boolean).join(" · "),
                       to: `/policy/${p.id}` };
          } },
        { key: "reports", label: "Rapporti giornalieri", icon: History,
          render: (r) => ({ line1: r.snippet || "—",
                            line2: `${r.date.split("-").reverse().join("/")}${r.author_name ? " · " + r.author_name : ""}`,
                            to: `/time-machine?d=${r.date}` }) },
    ];

    const totalCount = data ? groups.reduce((s, g) => s + (data[g.key]?.length || 0), 0) : 0;

    return (
        <div ref={rootRef} className="flex-1 max-w-lg relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
                data-testid="global-search"
                value={q}
                onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                onFocus={() => q.trim().length >= 2 && setOpen(true)}
                placeholder="Cerca clienti, progetti, note, task…"
                className="w-full pl-8 pr-3 py-1.5 text-sm bg-neutral-50 border border-neutral-200 rounded-md focus:bg-white focus:outline-none focus:ring-1 focus:ring-neutral-400"
            />
            {open && q.trim().length >= 2 && (
                <div data-testid="global-search-results"
                    className="absolute z-30 mt-1 left-0 right-0 bg-white border border-neutral-200 rounded-md shadow-lg max-h-[70vh] overflow-y-auto">
                    {loading && <div className="p-3 text-xs text-neutral-500">Ricerca…</div>}
                    {!loading && data && totalCount === 0 && (
                        <div className="p-4 text-sm text-neutral-500" data-testid="global-search-empty">Nessun risultato per "{q}"</div>
                    )}
                    {!loading && data && groups.map((g) => {
                        const items = data[g.key] || [];
                        if (items.length === 0) return null;
                        const Icon = g.icon;
                        return (
                            <div key={g.key} data-testid={`search-group-${g.key}`}>
                                <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold text-neutral-500 bg-neutral-50 border-b border-neutral-100 flex items-center gap-1.5">
                                    <Icon size={11} /> {g.label} · {items.length}
                                </div>
                                <ul>
                                    {items.map((it) => {
                                        const r = g.render(it);
                                        return (
                                            <li key={`${g.key}-${it.id}`}>
                                                <button onClick={() => go(r.to)}
                                                    data-testid={`search-item-${g.key}-${it.id}`}
                                                    className="w-full text-left px-3 py-2 hover:bg-neutral-50 border-b border-neutral-50 last:border-b-0">
                                                    <div className="text-sm text-neutral-900 truncate">{r.line1}</div>
                                                    {r.line2 && <div className="text-xs text-neutral-500 truncate">{r.line2}</div>}
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
